import React, { useState } from 'react';
import { View } from 'react-native';
import { CalcShell, FormulaLine, FormulaNote, NumericField } from '../../../components/shell';
import { DiagramCoilDeltaP } from '../../../components/diagrams';
import { usePersistedState } from '../../../hooks/usePersistedState';
import { useUnits } from '../../../context/UnitsContext';

const KEY = 'tabai.calc.hydronicCoilDeltaP.v1';

type Fields = {
  flowRate: string;
  cvValue: string;
  specificGravity: string;
  deltaP: string;
};
const EMPTY: Fields = { flowRate: '', cvValue: '', specificGravity: '', deltaP: '' };

/**
 * Hydronics — Coil Delta P
 *   GPM = Cv × √(ΔP / SG)
 * Solves for any missing field when the other 3 are supplied.
 */
const HydronicDeltaPCoilDeltaPScreen = () => {
  const [fields, setFields, { clear: clearPersisted }] = usePersistedState<Fields>(KEY, EMPTY);
  const [error, setError] = useState<string | null>(null);
  const [solvedFor, setSolvedFor] = useState<keyof Fields | null>(null);
  const { unit, toImp, fromImp, isImperial } = useUnits();

  const set = (patch: Partial<Fields>) => setFields((prev) => ({ ...prev, ...patch }));

  const calculate = () => {
    setError(null);
    const filled = (Object.values(fields) as string[]).filter((v) => v !== '').length;
    if (filled < 3) {
      setError('Fill any 3 of the 4 fields to solve for the missing one.');
      return;
    }
    if (filled === 4) {
      setError('All 4 values are filled — clear one to solve for it.');
      return;
    }

    const gpm = toImp(fields.flowRate, 'waterFlow');
    const cv = parseFloat(fields.cvValue);
    const sg = parseFloat(fields.specificGravity);
    const dp = parseFloat(fields.deltaP); // PSI — no unit conversion available

    if (fields.flowRate === '') {
      // GPM = Cv × √(ΔP / SG)
      if (!(cv > 0)) { setError('Cv value must be greater than zero.'); return; }
      if (dp < 0) { setError('Delta P cannot be negative.'); return; }
      if (!(sg > 0)) { setError('Specific gravity must be greater than zero.'); return; }
      setSolvedFor('flowRate');
      set({ flowRate: fromImp(cv * Math.sqrt(dp / sg), 'waterFlow', 1) });
    } else if (fields.cvValue === '') {
      // Cv = GPM / √(ΔP / SG)
      if (!(gpm > 0)) { setError('Flow rate must be greater than zero.'); return; }
      if (!(dp > 0)) { setError('Delta P must be greater than zero.'); return; }
      if (!(sg > 0)) { setError('Specific gravity must be greater than zero.'); return; }
      setSolvedFor('cvValue');
      set({ cvValue: (gpm / Math.sqrt(dp / sg)).toFixed(1) });
    } else if (fields.deltaP === '') {
      // ΔP = SG × (GPM / Cv)²
      if (!(gpm > 0)) { setError('Flow rate must be greater than zero.'); return; }
      if (!(cv > 0)) { setError('Cv value must be greater than zero.'); return; }
      if (!(sg > 0)) { setError('Specific gravity must be greater than zero.'); return; }
      setSolvedFor('deltaP');
      set({ deltaP: (sg * Math.pow(gpm / cv, 2)).toFixed(2) });
    } else if (fields.specificGravity === '') {
      // SG = ΔP / (GPM / Cv)²
      if (!(gpm > 0)) { setError('Flow rate must be greater than zero.'); return; }
      if (!(cv > 0)) { setError('Cv value must be greater than zero.'); return; }
      if (!(dp > 0)) { setError('Delta P must be greater than zero.'); return; }
      setSolvedFor('specificGravity');
      set({ specificGravity: (dp / Math.pow(gpm / cv, 2)).toFixed(3) });
    }
  };

  const clearAll = () => {
    setError(null);
    setSolvedFor(null);
    void clearPersisted();
  };

  const result = [
    fields.flowRate && { label: 'Flow Rate', value: fields.flowRate, unit: unit('waterFlow'), emphasis: solvedFor === 'flowRate' },
    fields.cvValue && { label: 'Cv Value', value: fields.cvValue, emphasis: solvedFor === 'cvValue' },
    fields.specificGravity && { label: 'Specific Gravity', value: fields.specificGravity, emphasis: solvedFor === 'specificGravity' },
    fields.deltaP && { label: 'Delta P', value: fields.deltaP, unit: 'PSI', emphasis: solvedFor === 'deltaP' },
  ].filter(Boolean) as { label: string; value: string; unit?: string; emphasis?: boolean }[];

  return (
    <CalcShell
      title="Coil Delta P"
      subtitle="Calculate pressure drop across heating/cooling coils using Cv values"
      formula={
        <>
          <FormulaLine>Q = Cv × √(ΔP / SG)</FormulaLine>
          <FormulaNote>
            Q = Flow Rate ({unit('waterFlow')}), Cv = Valve Coefficient (imperial), ΔP = Pressure Drop (PSI), SG = Specific Gravity.{'\n'}
            Note: Cv is defined in GPM/√PSI — ΔP is always entered in PSI.
          </FormulaNote>
        </>
      }
      error={error}
      onCalculate={calculate}
      onClear={clearAll}
      result={result.length ? result : null}
      copyFooter="Formula: Q = Cv × √(ΔP / SG)"
      diagram={<DiagramCoilDeltaP />}
      outputSlots={{
        gpm: fields.flowRate ? toImp(fields.flowRate, 'waterFlow') : undefined,
        cv: fields.cvValue ? parseFloat(fields.cvValue) : undefined,
        specific_gravity: fields.specificGravity ? parseFloat(fields.specificGravity) : undefined,
        delta_p_psi: fields.deltaP ? parseFloat(fields.deltaP) : undefined,
      }}
      onPrefill={(values) => {
        const patch: Partial<Fields> = {};
        if (values.gpm != null && !fields.flowRate) {
          patch.flowRate = fromImp(values.gpm, 'waterFlow', 1);
        }
        if (values.cv != null && !fields.cvValue) {
          patch.cvValue = values.cv.toFixed(1);
        }
        if (values.specific_gravity != null && !fields.specificGravity) {
          patch.specificGravity = values.specific_gravity.toFixed(3);
        }
        if (values.delta_p_psi != null && !fields.deltaP) {
          patch.deltaP = values.delta_p_psi.toFixed(2);
        }
        if (Object.keys(patch).length > 0) set(patch);
      }}
    >
      <View style={{ marginBottom: 12 }}>
        <NumericField
          label="Flow Rate (GPM)"
          value={fields.flowRate}
          onChangeText={(v) => set({ flowRate: v })}
          onSubmitEditing={calculate}
        />
      </View>
      <View style={{ marginBottom: 12 }}>
        <NumericField
          label="Cv Value"
          value={fields.cvValue}
          onChangeText={(v) => set({ cvValue: v })}
          onSubmitEditing={calculate}
        />
      </View>
      <View style={{ marginBottom: 12 }}>
        <NumericField
          label="Specific Gravity"
          value={fields.specificGravity}
          onChangeText={(v) => set({ specificGravity: v })}
          onSubmitEditing={calculate}
        />
      </View>
      <View style={{ marginBottom: 12 }}>
        <NumericField
          label="Delta P (PSI)"
          value={fields.deltaP}
          onChangeText={(v) => set({ deltaP: v })}
          onSubmitEditing={calculate}
        />
      </View>
    </CalcShell>
  );
};

export default HydronicDeltaPCoilDeltaPScreen;
